import type { ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useUnitFormatter } from 'hooks/useNumberFormatters'
import { getHotelDistanceToCruisePort } from 'lib/cruises/cruiseUtils'
import styled from 'styled-components'
import { rem } from 'polished'
import useOffer from 'hooks/Offers/useOffer'
import { isBedbankOffer } from 'lib/offer/offerTypes'
import offerPageURL from 'lib/offer/offerPageURL'
import Group from 'components/utils/Group'
import Image from 'components/Common/Image'
import Divider from 'LuxKit/Divider'
import BodyText from 'LuxKit/Typography/BodyText'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import TextButton from 'LuxKit/Button/TextButton'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'

const Tile = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) ${rem(128)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.XS};
  overflow: hidden;
`

const Content = styled(Group)`
  padding: ${rem(16)};
  min-width: 0;
`

const HotelImage = styled(Image)`
  height: 100%;
  min-height: ${rem(96)};
  object-fit: cover;
`

interface Props {
  offerId?: string
  departurePort?: App.CruisePort
}

export default function CruiseBundleHotelTile({ offerId, departurePort }: Props) {
  const intl = useIntl()
  const formatDistance = useUnitFormatter({ maximumFractionDigits: 1 })
  const [offer, fetching, error] = useOffer(offerId)

  if (fetching || error || !offer || !isBedbankOffer(offer)) return null

  const distance = getHotelDistanceToCruisePort(offer.property, departurePort)
  const location = [offer.property.address.city, offer.property.address.countryName].filter(Boolean).join(', ')

  return <Group direction="vertical" gap={16}>
    <Divider kind="primary" />
    <BodyTextBlock
      variant="large"
      weight="bold"
      startIcon={<LineBedIcon colour="highlight-primary-dark" size="S" />}
    >
      {intl.formatMessage({
        id: '2vROtVOP7ozGLA2R5eEG',
        defaultMessage: 'Hotel stay',
        description: 'Section heading above the included hotel preview on the cruise offer page',
      })}
    </BodyTextBlock>
    <Group direction="vertical" gap={8}>
      <BodyTextBlock variant="large" weight="bold">
        {intl.formatMessage({
          id: 'lDKTh7gUsL9WwQp7fv-f',
          defaultMessage: 'Pre-cruise stay',
          description: 'Heading above the bundled hotel tile for the stay before departure on a cruise offer page',
        })}
      </BodyTextBlock>
      <Tile>
        <Content direction="vertical" tabletDirection="horizontal" gap={16} verticalAlign="center" horizontalAlign="space-between">
          <Group direction="vertical" gap={4}>
            <BodyText variant="small" colour="light-foreground-alt">{location}</BodyText>
            <BodyText variant="medium" weight="bold">
              {intl.formatMessage({
                id: 'PPMM_KiEBw8n7K5qCDh9',
                defaultMessage: '{hotelName} or similar',
                description: 'Hotel preview title indicating an example hotel available with a cruise and hotel bundle',
              }, { hotelName: offer.name })}
            </BodyText>
            {distance !== undefined && <BodyText variant="medium" colour="light-foreground-alt">
              <FormattedMessage
                id="S_ceNF7CvxGI70_NneZT"
                defaultMessage="Approx. {distance} from your departure port (<bold>{portName}</bold>)"
                description="Approximate straight-line distance from the bundled hotel to the cruise departure port"
                values={{
                  distance: formatDistance(distance, 'kilometer'),
                  portName: departurePort?.name,
                  bold: renderPortName,
                }}
              />
            </BodyText>}
          </Group>
          <TextButton kind="secondary" size="small" href={offerPageURL(offer)} target="_blank" rel="noopener noreferrer">
            {intl.formatMessage({
              id: 'cNFfeu9R5jTXz8awvWyb',
              defaultMessage: 'View details',
              description: 'Button opening the hotel offer page from the included hotel preview on a cruise offer',
            })}
          </TextButton>
        </Content>
        <HotelImage image={offer.image} fit="center" objectFit="cover" alt={offer.name} width={128} height={96} />
      </Tile>
    </Group>
  </Group>
}

function renderPortName(chunks: ReactNode) {
  return <BodyText key="port-name" as="span" variant="medium" weight="bold" colour="light-foreground-alt">{chunks}</BodyText>
}
